import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { AionSectionCard } from "@/components/aion/AionSectionCard";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  acknowledgePlatformConflict,
  acknowledgePlatformDeadLetter,
  applyPlatformMigration,
  createPlatformBackup,
  createPlatformOTAPackage,
  deletePlatformBackup,
  deployPlatformOTA,
  getPlatformBackups,
  getPlatformGoLiveHealth,
  getPlatformMigrations,
  getPlatformOTAPackages,
  getPlatformPhase15Summary,
  recoverPlatformCore,
  restorePlatformBackup,
  retryPlatformConflict,
  retryPlatformDeadLetter,
  retryPlatformFailedSync,
  updatePlatformReleaseChannel,
  verifyPlatformBackup,
} from "@/services/api";

const packageDefaults = { version: "15.0.0", channel: "STABLE", release_notes: "Phase 15 operational package" };
const migrationDefaults = { migration_version: "15.0.0", target_schema_version: "2026.15.0", description: "Phase 15 schema alignment" };


const ConfirmPhase15Action = ({
  buttonLabel,
  confirmLabel,
  confirmTestId,
  description,
  disabled,
  onConfirm,
  pending,
  title,
  triggerTestId,
  variant = "outline",
}) => (
  <AlertDialog>
    <AlertDialogTrigger asChild>
      <Button variant={variant} disabled={disabled || pending} data-testid={triggerTestId}>{buttonLabel}</Button>
    </AlertDialogTrigger>
    <AlertDialogContent className="aion-confirm-dialog overflow-hidden border-white/70 bg-background/95 shadow-[0_36px_90px_rgba(28,25,23,0.22)] backdrop-blur-xl sm:rounded-[28px]">
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Abbrechen</AlertDialogCancel>
        <AlertDialogAction onClick={onConfirm} disabled={pending} data-testid={confirmTestId}>
          {pending ? "Läuft …" : confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const AdminPhase15Panel = ({ installations = [], systemSummary = null, operatorRole = "PLATFORM_ADMIN", token, onRefresh }) => {
  const normalizedRole = String(operatorRole || "PLATFORM_ADMIN").toUpperCase();
  const canRestoreRecovery = ["PLATFORM_ADMIN", "MANAGER", "INSTALLER", "TECHNICIAN"].includes(normalizedRole);
  const canMigration = ["PLATFORM_ADMIN", "MANAGER"].includes(normalizedRole);
  const initialInstallation = installations[0] || null;
  const [selectedInstallationId, setSelectedInstallationId] = useState(initialInstallation?.id || initialInstallation?.installation_id || "");
  const [selectedTenantId, setSelectedTenantId] = useState(initialInstallation?.tenant_id || "tenant-default");
  const [backups, setBackups] = useState([]);
  const [packages, setPackages] = useState([]);
  const [migrations, setMigrations] = useState([]);
  const [goLive, setGoLive] = useState(null);
  const [phase15, setPhase15] = useState(systemSummary?.phase15 || null);
  const [backupType, setBackupType] = useState("CONFIG");
  const [feedback, setFeedback] = useState("");
  const [loadWarning, setLoadWarning] = useState("");
  const [pending, setPending] = useState("");
  const [recoveryCoreId, setRecoveryCoreId] = useState("");
  const [restoreBackupId, setRestoreBackupId] = useState("");
  const [deployPackageId, setDeployPackageId] = useState("");
  const [otaChannel, setOtaChannel] = useState("STABLE");
  const [packageDraft, setPackageDraft] = useState(packageDefaults);
  const [migrationDraft, setMigrationDraft] = useState(migrationDefaults);

  useEffect(() => {
    if (!selectedInstallationId && installations[0]) {
      setSelectedInstallationId(installations[0].id || installations[0].installation_id || "");
      setSelectedTenantId(installations[0].tenant_id || "tenant-default");
      setOtaChannel(installations[0].ota_channel || "STABLE");
    }
  }, [installations, selectedInstallationId]);

  const selectedInstallation = useMemo(() => installations.find((item) => (item.id || item.installation_id) === selectedInstallationId) || null, [installations, selectedInstallationId]);

  const refreshData = useCallback(async () => {
    if (!selectedInstallationId || !selectedTenantId) return;
    const results = await Promise.allSettled([
      getPlatformBackups(selectedInstallationId, selectedTenantId, token),
      getPlatformOTAPackages(token),
      getPlatformMigrations(selectedInstallationId, selectedTenantId, token),
      getPlatformGoLiveHealth(selectedInstallationId, selectedTenantId, token),
      getPlatformPhase15Summary(token, selectedTenantId),
    ]);
    const [backupResult, packageResult, migrationResult, goLiveResult, summaryResult] = results;
    if (backupResult.status === "fulfilled") setBackups(backupResult.value);
    if (packageResult.status === "fulfilled") setPackages(packageResult.value);
    if (migrationResult.status === "fulfilled") setMigrations(migrationResult.value);
    if (goLiveResult.status === "fulfilled") setGoLive(goLiveResult.value);
    if (summaryResult.status === "fulfilled") setPhase15(summaryResult.value);
    const failedCount = results.filter((item) => item.status === "rejected").length;
    setLoadWarning(failedCount ? `${failedCount} Phase-15-Bereich(e) konnten nicht geladen werden. Verfügbare Daten bleiben sichtbar.` : "");
  }, [selectedInstallationId, selectedTenantId, token]);

  useEffect(() => {
    if (selectedInstallationId && selectedTenantId) {
      void refreshData();
    }
  }, [refreshData, selectedInstallationId, selectedTenantId]);

  useEffect(() => {
    if (selectedInstallation) {
      setSelectedTenantId(selectedInstallation.tenant_id || "tenant-default");
      setOtaChannel(selectedInstallation.ota_channel || "STABLE");
      setRecoveryCoreId(selectedInstallation.core_id ? `${selectedInstallation.core_id}-recovery` : "core-phase15-recovery");
    }
  }, [selectedInstallation]);

  const runAction = async (key, callback, successMessage) => {
    setPending(key);
    setFeedback("");
    try {
      await callback();
      const refreshResults = await Promise.allSettled([
        refreshData(),
        Promise.resolve().then(() => onRefresh?.()),
      ]);
      const refreshFailed = refreshResults.some((item) => item.status === "rejected");
      setFeedback(
        refreshFailed
          ? `${successMessage} Status konnte nicht vollständig neu geladen werden.`
          : successMessage,
      );
    } catch (error) {
      setFeedback(error.message || "Phase 15 action failed.");
    } finally {
      setPending("");
    }
  };

  const review = phase15?.local_core_review || { summary: {}, conflicts: [], dead_letters: [], failed_sync: [] };

  return (
    <div className="aion-admin-stack" data-testid="admin-phase15-panel">
      {feedback ? <div className="aion-note" data-testid="admin-phase15-feedback">{feedback}</div> : null}
      {loadWarning ? <div className="aion-error-box" data-testid="admin-phase15-load-warning">{loadWarning}</div> : null}

      <AionSectionCard title="BACKUPS" eyebrow="PHASE 15" subtitle="FULL, INCREMENTAL und CONFIG Backups mit Prüfsumme, Aufbewahrung und Audit." testId="admin-phase15-backups-card">
        <div className="aion-settings-grid" data-testid="admin-phase15-backups-grid">
          <label className="aion-settings-field" data-testid="admin-phase15-installation-field"><span>Installation</span><Select value={selectedInstallationId || "none"} onValueChange={(value) => setSelectedInstallationId(value === "none" ? "" : value)}><SelectTrigger data-testid="admin-phase15-installation-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="none">Select</SelectItem>{installations.map((item) => <SelectItem key={item.id || item.installation_id} value={item.id || item.installation_id}>{item.name}</SelectItem>)}</SelectContent></Select></label>
          <label className="aion-settings-field" data-testid="admin-phase15-backup-type-field"><span>Backup type</span><Select value={backupType} onValueChange={setBackupType}><SelectTrigger data-testid="admin-phase15-backup-type-select"><SelectValue /></SelectTrigger><SelectContent>{["FULL", "INCREMENTAL", "CONFIG"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></label>
        </div>
        <div className="aion-inline-actions" data-testid="admin-phase15-backup-actions">
          <Button disabled={pending === "backup-create" || !selectedInstallationId} onClick={() => runAction("backup-create", () => createPlatformBackup(selectedInstallationId, { tenant_id: selectedTenantId, installation_id: selectedInstallationId, backup_type: backupType, reason: `phase15_${backupType.toLowerCase()}` }, token), "Backup created.")} data-testid="admin-phase15-create-backup-button">Create backup</Button>
          <Button variant="outline" disabled={pending === "backup-verify" || !backups[0]?.backup_id} onClick={() => runAction("backup-verify", () => verifyPlatformBackup(selectedInstallationId, backups[0].backup_id, { tenant_id: selectedTenantId, installation_id: selectedInstallationId }, token), "Latest backup verified.")} data-testid="admin-phase15-verify-backup-button">Verify latest</Button>
          <ConfirmPhase15Action
            buttonLabel="Delete latest"
            confirmLabel="Backup endgültig löschen"
            triggerTestId="admin-phase15-delete-backup-button"
            confirmTestId="admin-phase15-delete-backup-confirm"
            title="Backup wirklich löschen?"
            description="Das ausgewählte Backup wird aus dem bestehenden Phase-15-Backup-Pfad entfernt. Diese Aktion erfordert eine ausdrückliche Bestätigung."
            disabled={!backups[0]?.backup_id}
            pending={pending === "backup-delete"}
            onConfirm={() => runAction("backup-delete", () => deletePlatformBackup(selectedInstallationId, backups[0].backup_id, selectedTenantId, token), "Latest backup deleted.")}
          />
        </div>
        <div className="aion-list" data-testid="admin-phase15-backup-list">
          {backups.slice(0, 5).map((item) => <div className="aion-list-row" key={item.backup_id} data-testid={`admin-phase15-backup-${item.backup_id}`}><div className="aion-list-copy"><strong>{item.backup_type}</strong><small>{item.backup_id} · verify {item.verification_status} · retain {item.retention?.days}d</small></div></div>)}
        </div>
      </AionSectionCard>

      {canRestoreRecovery ? (
        <>
        <AionSectionCard title="RESTORE" eyebrow="CONTROLLED RESTORE" subtitle="Sicherheits-Backup vor Restore, Kompatibilitätsprüfung und Rollback bei Fehlern." testId="admin-phase15-restore-card">
          <div className="aion-settings-grid" data-testid="admin-phase15-restore-grid">
            <label className="aion-settings-field" data-testid="admin-phase15-restore-id-field"><span>Backup ID</span><Input value={restoreBackupId} onChange={(event) => setRestoreBackupId(event.target.value)} placeholder={backups[0]?.backup_id || "backup-id"} data-testid="admin-phase15-restore-id-input" /></label>
          </div>
          <ConfirmPhase15Action
            buttonLabel="Restore backup"
            confirmLabel="Restore starten"
            triggerTestId="admin-phase15-restore-button"
            confirmTestId="admin-phase15-restore-confirm"
            title="Backup wirklich wiederherstellen?"
            description="Der Restore kann den aktuellen Software-/Konfigurationszustand dieser Installation verändern. AION nutzt weiterhin den bestehenden kontrollierten Restore- und Rollback-Pfad."
            disabled={!selectedInstallationId || !(restoreBackupId || backups[0]?.backup_id)}
            pending={pending === "restore"}
            onConfirm={() => runAction("restore", () => restorePlatformBackup(selectedInstallationId, { tenant_id: selectedTenantId, installation_id: selectedInstallationId, backup_id: restoreBackupId || backups[0]?.backup_id, reason: "phase15_restore" }, token), "Restore completed.")}
            variant="default"
          />
        </AionSectionCard>
  
        <AionSectionCard title="RECOVERY" eyebrow="CORE RECOVERY" subtitle="Replacement Core enrollment, old Core trust revocation and software-side recovery only." testId="admin-phase15-recovery-card">
          <div className="aion-settings-grid" data-testid="admin-phase15-recovery-grid">
            <label className="aion-settings-field" data-testid="admin-phase15-recovery-core-field"><span>Replacement Core ID</span><Input value={recoveryCoreId} onChange={(event) => setRecoveryCoreId(event.target.value)} data-testid="admin-phase15-recovery-core-input" /></label>
          </div>
          <ConfirmPhase15Action
            buttonLabel="Recover Core"
            confirmLabel="Core-Recovery starten"
            triggerTestId="admin-phase15-recovery-button"
            confirmTestId="admin-phase15-recovery-confirm"
            title="Core-Recovery wirklich starten?"
            description="Der bestehende Core wird im Recovery-Pfad ersetzt bzw. widerrufen. Prüfe Replacement Core ID, Version und Schema vor der Bestätigung."
            disabled={!selectedInstallationId || !recoveryCoreId}
            pending={pending === "recovery"}
            onConfirm={() => runAction("recovery", () => recoverPlatformCore(selectedInstallationId, { tenant_id: selectedTenantId, installation_id: selectedInstallationId, replacement_core_id: recoveryCoreId, replacement_device_identity: recoveryCoreId, replacement_version: packageDraft.version, replacement_schema_version: migrationDraft.target_schema_version, reason: "phase15_recovery" }, token), "Core recovery completed.")}
            variant="default"
          />
        </AionSectionCard>
          </>
      ) : null}


      <AionSectionCard title="UPDATES" eyebrow="OTA" subtitle="Channels DEV/STAGING/BETA/STABLE, checksum validation, metadata signature and rollback after health failure." testId="admin-phase15-updates-card">
        <div className="aion-settings-grid" data-testid="admin-phase15-updates-grid">
          <label className="aion-settings-field" data-testid="admin-phase15-package-version-field"><span>Package version</span><Input value={packageDraft.version} onChange={(event) => setPackageDraft((prev) => ({ ...prev, version: event.target.value }))} data-testid="admin-phase15-package-version-input" /></label>
          <label className="aion-settings-field" data-testid="admin-phase15-channel-field"><span>Channel</span><Select value={packageDraft.channel} onValueChange={(value) => setPackageDraft((prev) => ({ ...prev, channel: value }))}><SelectTrigger data-testid="admin-phase15-channel-select"><SelectValue /></SelectTrigger><SelectContent>{["DEV", "STAGING", "BETA", "STABLE"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></label>
        </div>
        <div className="aion-inline-actions" data-testid="admin-phase15-update-actions">
          <Button disabled={pending === "ota-package"} onClick={() => runAction("ota-package", () => createPlatformOTAPackage({ ...packageDraft, manifest: { modules: ["backup", "ota", "local-core"], version: packageDraft.version } }, token), "OTA package created.")} data-testid="admin-phase15-create-package-button">Create package</Button>
          <Button variant="outline" disabled={pending === "ota-channel" || !selectedInstallationId} onClick={() => runAction("ota-channel", () => updatePlatformReleaseChannel({ tenant_id: selectedTenantId, installation_id: selectedInstallationId, channel: otaChannel }, token), "Release channel updated.")} data-testid="admin-phase15-set-channel-button">Set channel</Button>
          <ConfirmPhase15Action
            buttonLabel="Deploy update"
            confirmLabel="OTA-Deployment starten"
            triggerTestId="admin-phase15-deploy-button"
            confirmTestId="admin-phase15-deploy-confirm"
            title="OTA-Update wirklich deployen?"
            description="Das ausgewählte Paket wird über den bestehenden OTA-Pfad auf die gewählte Installation angewendet. Checksum-, Kompatibilitäts- und Health-Prüfungen bleiben aktiv."
            disabled={!selectedInstallationId || !(deployPackageId || packages[0]?.package_id)}
            pending={pending === "ota-deploy"}
            onConfirm={() => runAction("ota-deploy", () => deployPlatformOTA({ tenant_id: selectedTenantId, installation_id: selectedInstallationId, package_id: deployPackageId || packages[0]?.package_id, reason: "phase15_ota" }, token), "OTA deployment finished.")}
          />
        </div>
        <label className="aion-settings-field" data-testid="admin-phase15-deploy-package-field"><span>Deploy package ID</span><Input value={deployPackageId} onChange={(event) => setDeployPackageId(event.target.value)} placeholder={packages[0]?.package_id || "pkg-id"} data-testid="admin-phase15-deploy-package-input" /></label>
        <label className="aion-settings-field" data-testid="admin-phase15-channel-update-field"><span>Installation channel</span><Select value={otaChannel} onValueChange={setOtaChannel}><SelectTrigger data-testid="admin-phase15-channel-update-select"><SelectValue /></SelectTrigger><SelectContent>{["DEV", "STAGING", "BETA", "STABLE"].map((item) => <SelectItem key={item} value={item}>{item}</SelectItem>)}</SelectContent></Select></label>
      </AionSectionCard>

      <AionSectionCard title="LOCAL CORE" eyebrow="RESOLVE & RETRY" subtitle="Konflikte, Dead Letters und failed sync items mit sicherem Retry oder Acknowledge – niemals force-success." testId="admin-phase15-local-core-card">
        <div className="aion-compact-status-grid" data-testid="admin-phase15-local-core-grid">
          <div className="aion-compact-status-box" data-testid="admin-phase15-conflict-count"><span>Conflicts</span><strong>{review.summary?.conflicts ?? 0}</strong></div>
          <div className="aion-compact-status-box" data-testid="admin-phase15-dead-letter-count"><span>Dead letters</span><strong>{review.summary?.dead_letters ?? 0}</strong></div>
          <div className="aion-compact-status-box" data-testid="admin-phase15-failed-sync-count"><span>Failed sync</span><strong>{review.summary?.failed_sync ?? 0}</strong></div>
        </div>
        <div className="aion-list" data-testid="admin-phase15-review-list">
          {[...(review.conflicts || []).slice(0, 2).map((item) => ({ ...item, type: "conflict" })), ...(review.dead_letters || []).slice(0, 2).map((item) => ({ ...item, type: "dead" })), ...(review.failed_sync || []).slice(0, 2).map((item) => ({ ...item, type: "failed" }))].map((item) => (
            <div className="aion-list-row" key={`${item.type}-${item.id || item.event_id}`} data-testid={`admin-phase15-review-${item.type}-${item.id || item.event_id}`}>
              <div className="aion-list-copy">
                <strong>{item.type.toUpperCase()} · {item.resource_type || item.event_type}</strong>
                <small>{item.reason || item.dead_letter_reason || item.last_error || "review"} · core {item.core_id || "—"} · installation {item.installation_id || "—"}</small>
              </div>
              <div className="aion-inline-actions">
                {item.type === "conflict" ? <Button variant="outline" onClick={() => runAction(`ack-conflict-${item.id}`, () => acknowledgePlatformConflict(item.id, { note: "reviewed" }, token), "Conflict acknowledged.")} data-testid={`admin-phase15-ack-conflict-${item.id}`}>Ack</Button> : null}
                {item.type === "dead" ? <Button variant="outline" onClick={() => runAction(`ack-dead-${item.event_id}`, () => acknowledgePlatformDeadLetter(item.event_id, { note: "reviewed" }, token), "Dead letter acknowledged.")} data-testid={`admin-phase15-ack-dead-${item.event_id}`}>Ack</Button> : null}
                {item.safe_retry_allowed ? (
                  <Button onClick={() => runAction(`retry-${item.type}-${item.id || item.event_id}`, () => item.type === "conflict" ? retryPlatformConflict(item.id, { note: "safe_retry" }, token) : item.type === "dead" ? retryPlatformDeadLetter(item.event_id, { note: "safe_retry" }, token) : retryPlatformFailedSync(item.event_id, { note: "safe_retry" }, token), "Safe retry requested.")} data-testid={`admin-phase15-retry-${item.type}-${item.id || item.event_id}`}>Retry</Button>
                ) : <span className="aion-note" data-testid={`admin-phase15-retry-disabled-${item.type}-${item.id || item.event_id}`}>{item.safe_retry_reason || "unsafe"}</span>}
              </div>
            </div>
          ))}
        </div>
      </AionSectionCard>

      <AionSectionCard title="GO-LIVE HEALTH" eyebrow="INSTALLER READINESS" subtitle="PASS / WARNING / FAIL / NOT_APPLICABLE / UNAVAILABLE Status für Runtime, Queue, Trust, Backup und OTA." testId="admin-phase15-golive-card">
        <div className="aion-note" data-testid="admin-phase15-golive-status">Overall: {goLive?.status || "UNAVAILABLE"}</div>
        <div className="aion-list" data-testid="admin-phase15-golive-list">
          {(goLive?.checks || []).map((item) => <div className="aion-list-row" key={item.key} data-testid={`admin-phase15-golive-${item.key}`}><div className="aion-list-copy"><strong>{item.key}</strong><small>{item.status} · {item.detail}</small></div></div>)}
        </div>
      </AionSectionCard>

      {canMigration ? (
        <AionSectionCard title="MIGRATIONS" eyebrow="SCHEMA" subtitle="Migration Version und Status werden verfolgt. Unsichere oder destruktive Migrationen werden abgelehnt." testId="admin-phase15-migrations-card">
          <div className="aion-settings-grid" data-testid="admin-phase15-migration-grid">
            <label className="aion-settings-field" data-testid="admin-phase15-migration-version-field"><span>Migration version</span><Input value={migrationDraft.migration_version} onChange={(event) => setMigrationDraft((prev) => ({ ...prev, migration_version: event.target.value }))} data-testid="admin-phase15-migration-version-input" /></label>
            <label className="aion-settings-field" data-testid="admin-phase15-migration-schema-field"><span>Target schema</span><Input value={migrationDraft.target_schema_version} onChange={(event) => setMigrationDraft((prev) => ({ ...prev, target_schema_version: event.target.value }))} data-testid="admin-phase15-migration-schema-input" /></label>
          </div>
          <ConfirmPhase15Action
            buttonLabel="Apply migration"
            confirmLabel="Migration anwenden"
            triggerTestId="admin-phase15-apply-migration-button"
            confirmTestId="admin-phase15-apply-migration-confirm"
            title="Schema-Migration wirklich anwenden?"
            description="Die Migration aktualisiert den bestehenden Schema-Stand. Destruktive Migrationen bleiben serverseitig abgelehnt; diese Bestätigung ersetzt keine Backend-Sicherheitsprüfung."
            disabled={!selectedInstallationId}
            pending={pending === "migration"}
            onConfirm={() => runAction("migration", () => applyPlatformMigration(selectedInstallationId, { tenant_id: selectedTenantId, installation_id: selectedInstallationId, migration_version: migrationDraft.migration_version, target_schema_version: migrationDraft.target_schema_version, description: migrationDraft.description, destructive: false }, token), "Migration applied.")}
            variant="default"
          />
          <div className="aion-list" data-testid="admin-phase15-migration-list">
            {migrations.slice(0, 5).map((item) => <div className="aion-list-row" key={item.migration_id} data-testid={`admin-phase15-migration-${item.migration_id}`}><div className="aion-list-copy"><strong>{item.migration_version}</strong><small>{item.status} · schema {item.target_schema_version}</small></div></div>)}
          </div>
        </AionSectionCard>
        ) : null}
    </div>
  );
};